import Vue from 'vue'
import api from '@/api_alv'
import i18n from '@/assets/i18n/i18n'

const DIR = 'account/companies'
const DEFAULT_ACCOUNT_COMPANIES = {
    "id": 0,
    "alvId": "ALV000000000AC", // user cannot setup new ALV customer, so does this make sense or default should be null?
    "publishingCompanies": [],
    "labelCompanies": [],
    // Column 1
    "companyName": null,
    "salutation1": null,
    "firstname": null,
    "lastname": null,
    "addressRow1": null,
    "addressRow2": null,
    "zip": null,
    "city": null,
    "state":null,
    "country": null,
    // Column 2
    "customerNumber": null,
    "salutation2": null,
    "formOfAddress": null,
    "accountHolder": null,
    "iban": null,
    "bic": null,
    "email": null,
    "cellphone": null,
    "phone": null,
    // Column 3
    "alvCode": null,
    "alvPackage": null,
    "vatPercent": null,
    "sepaMandate": null,
    "sepaMandateDate": null,
    // Column 4
    "role": null,
    "language": null,
    "numberFormat": null,
    "currency": null,
    "homeSocietyCode": null,
    "storageUsed": null,
    "storageAvailable": null,
}

const DEFAULT_PUBLISHING_COMPANIES = {
    "id": 0,
    // Column 1
    "companyName": null,
    "companyAsAzEntry": null,
    "companyAsAzList": [],
    "addressRow1": null,
    "addressRow2": null,
    "zip": null,
    "city": null,
    "state":null,
    "country": null,
    "email": null,
    "statementText": null,
    // Column 2
    "senderRow": null,
    "ykto": null,
    "hkto": null,
    "ukto": null,
    "ipi": null,
    "footerLeft": null,
    // Column 3
    "invoicesNumberRange": null,
    "shippingNumberRange": null,
    "creditNumberRange": null,
    "statementNumberRange": null,
    "cwrSubmitterCode": null,
    "footerMiddle": null,
    // Column 4
    "invoicesNextValue": null,
    "shippingNextValue": null,
    "creditNextValue": null,
    "statementNextValue": null,
    "vatId": null,
    "footerRight": null,
}

const DEFAULT_LABEL_COMPANIES = {
    "id": 0,
    // Column 1
    "companyName": null,
    "addressRow1": null,
    "addressRow2": null,
    "zip": null,
    "city": null,
    "state":null,
    "country": null,
    "statementText": null,
    // Column 2
    "senderRow": null,
    "gemaTasAudio": null,
    "gemaTasVideo": null,
    "gemaLicenseeNumber": null,
    "footerLeft": null,
    // Column 3
    "invoicesNumberRange": null,
    "shippingNumberRange": null,
    "creditNumberRange": null,
    "statementNumberRange": null,
    "footerMiddle": null,
    // Column 4
    "invoicesNextValue": null,
    "shippingNextValue": null,
    "creditNextValue": null,
    "statementNextValue": null,
    "footerRight": null,
    "vatId": null,
    "customsId" : null
}

const state = {
    defaultNewAccount: DEFAULT_ACCOUNT_COMPANIES,
    accountDetails: {},
    accountUniqueId: '',
    accountCompaniesLabelCompanies: {},
    accountCompaniesPublishingCompanies: {},

    // PUBLISHING COMPANIES Panel
    accountPublishingCompanies: [],
    selectedAccountPublishingCompanies: [],
    defaultAccountPublishingCompanies: DEFAULT_PUBLISHING_COMPANIES,
    accountPublishingCompaniesEditingDialogDetails: DEFAULT_PUBLISHING_COMPANIES,
    showAccountPublishingCompaniesEditingDialog: false,
    showPayableConfirmationDialog:false,
    payableConfirmationDialogContext:'',
    addNewPublishingCompany:false,

    // LABEL COMPANIES Panel
    accountLabelCompanies: [],
    selectedAccountLabelCompanies: [],
    defaultAccountLabelCompanies: DEFAULT_LABEL_COMPANIES,
    accountLabelCompaniesEditingDialogDetails: DEFAULT_LABEL_COMPANIES,
    showAccountLabelCompaniesEditingDialog: false,
    addNewLabelCompany:false,

    // INVOICES Panel
    accountInvoices: [],

    // COMPANY DEFAULT MAPPINGS Dialog
    // shown for one publishing/label company at a time
    showCompanyDefaultMappingsDialog: false,
    companyDefaultMappingsContext: { companyId: null, companyType: null, companyName: null },
    companyDefaultMappings: [], // raw `defaults` array from GET/PUT, keys are BE column names
    // pool cache keyed by `${statementType}|${language}` -> { yours: [...], system: [...], loading: Boolean }
    companyDefaultMappingsPools: {},
}

const getters = {
    getAccountUniqueId(state)                                   { return state.accountUniqueId },
    getAccountDetails(state)                                    { return state.accountDetails },
    getAccountDetailsAlvId(state)                               { return state.accountDetails.alvId },
    
    //#region MAIN FORM (Getters) 
    // Column 1
    getAccountDetailsCompanyName(state)                         { return state.accountDetails.companyName },
    getAccountDetailsSalutation1(state)                         { return state.accountDetails.salutation1 },
    getAccountDetailsFirstname(state)                           { return state.accountDetails.firstname },
    getAccountDetailsLastname(state)                            { return state.accountDetails.lastname },
    getAccountDetailsAddressRow1(state)                         { return state.accountDetails.addressRow1 },
    getAccountDetailsAddressRow2(state)                         { return state.accountDetails.addressRow2 },
    getAccountDetailsZip(state)                                 { return state.accountDetails.zip },
    getAccountDetailsCity(state)                                { return state.accountDetails.city },
    getAccountDetailsState(state)                               { return state.accountDetails.state },
    getAccountDetailsCountry(state)                             { return state.accountDetails.country },

    // Column 2
    getAccountDetailsCustomerNumber(state)                      { return state.accountDetails.customerNumber },
    getAccountDetailsSalutation2(state)                         { return state.accountDetails.salutation2 },
    getAccountDetailsFormalSalutation(state)                    { return state.accountDetails.formalSalutation },
    getAccountDetailsAccountHolder(state)                       { return state.accountDetails.accountHolder },
    getAccountDetailsIban(state)                                { return state.accountDetails.iban },
    getAccountDetailsBic(state)                                 { return state.accountDetails.bic },
    getAccountDetailsEmail(state)                               { return state.accountDetails.email },
    getAccountDetailsCellphone(state)                           { return state.accountDetails.cellphone },
    getAccountDetailsPhone(state)                               { return state.accountDetails.phone },
    
    // Column 3
    getAccountDetailsAlvCode(state)                             { return state.accountDetails.alvCode },
    getAccountDetailsAlvPackage(state)                          { return state.accountDetails.alvPackage },
    getAccountDetailsVatPercent(state)                          { return state.accountDetails.vatPercent },
    getAccountDetailsSepaMandate(state)                         { return state.accountDetails.sepaMandate },
    getAccountDetailsSepaMandateDate(state)                     { return state.accountDetails.sepaMandateDate },

    // Column 4
    getAccountDetailsLanguage(state)                            { return state.accountDetails.language },
    getAccountDetailsNumberFormat(state)                        { return state.accountDetails.numberFormat },
    getAccountDetailsCurrency(state)                            { return state.accountDetails.currency },
    getAccountDetailsHomeSocietyCode(state)                     { return state.accountDetails.homeSocietyCode },
    getAccountDetailsStorageUsed(state)                         { return state.accountDetails.storageUsed },
    getAccountDetailsStorageAvailable(state)                    { return state.accountDetails.storageAvailable },
    //#endregion

    //#region Publishing Companies PANEL (Getters)
    getAccountPublishingCompanies(state)                        { return state.accountPublishingCompanies },
    getSelectedAccountPublishingCompanies(state)                { return state.selectedAccountPublishingCompanies },
    getShowAccountPublishingCompaniesEditingDialog(state)       { return state.showAccountPublishingCompaniesEditingDialog },
    getShowPayableConfirmationDialog(state)                     { return state.showPayableConfirmationDialog },
    getPayableConfirmationDialogContext(state)                  { return state.payableConfirmationDialogContext },
    //#endregion

    //#region Publishing Companies EDIT DIALOG (Getters)
    getDefaultAccountPublishingCompanies(state)                 { return state.defaultAccountPublishingCompanies },
    getAccountPublishingCompaniesEditingDialogDetails(state)    { return state.accountPublishingCompaniesEditingDialogDetails },
    getAddNewPublishingCompany(state)                           { return state.addNewPublishingCompany },

    // Column 1
    getAccountPublishingCompaniesDetailsCompanyName(state)      { return state.accountPublishingCompaniesEditingDialogDetails.companyName },
    getAccountPublishingCompaniesDetailsCompanyAsAzEntry(state) { return state.accountPublishingCompaniesEditingDialogDetails.companyAsAzEntry },
    getAccountPublishingCompaniesDetailsCompanyAsAzList(state)  { return state.accountPublishingCompaniesEditingDialogDetails.companyAsAzList },
    getAccountPublishingCompaniesDetailsAddressRow1(state)      { return state.accountPublishingCompaniesEditingDialogDetails.addressRow1 },
    getAccountPublishingCompaniesDetailsAddressRow2(state)      { return state.accountPublishingCompaniesEditingDialogDetails.addressRow2 },
    getAccountPublishingCompaniesDetailsZip(state)              { return state.accountPublishingCompaniesEditingDialogDetails.zip },
    getAccountPublishingCompaniesDetailsCity(state)             { return state.accountPublishingCompaniesEditingDialogDetails.city },
    getAccountPublishingCompaniesDetailsState(state)             { return state.accountPublishingCompaniesEditingDialogDetails.state },
    getAccountPublishingCompaniesDetailsCountry(state)          { return state.accountPublishingCompaniesEditingDialogDetails.country },
    getAccountPublishingCompaniesDetailsEmail(state)            { return state.accountPublishingCompaniesEditingDialogDetails.email },
    getAccountPublishingCompaniesDetailsStatementText(state)    { return state.accountPublishingCompaniesEditingDialogDetails.statementText },
    
    // Column 2
    getAccountPublishingCompaniesDetailsSenderRow(state)        { return state.accountPublishingCompaniesEditingDialogDetails.senderRow },
    getAccountPublishingCompaniesDetailsYkto(state)             { return state.accountPublishingCompaniesEditingDialogDetails.ykto },
    getAccountPublishingCompaniesDetailsHkto(state)             { return state.accountPublishingCompaniesEditingDialogDetails.hkto },
    getAccountPublishingCompaniesDetailsUkto(state)             { return state.accountPublishingCompaniesEditingDialogDetails.ukto },
    getAccountPublishingCompaniesDetailsIpi(state)              { return state.accountPublishingCompaniesEditingDialogDetails.ipi },
    getAccountPublishingCompaniesDetailsFooterLeft(state)       { return state.accountPublishingCompaniesEditingDialogDetails.footerLeft },

    // Column 3
    getAccountPublishingCompaniesDetailsInvoicesNumberRange(state)  { return state.accountPublishingCompaniesEditingDialogDetails.invoicesNumberRange },
    getAccountPublishingCompaniesDetailsShippingNumberRange(state)  { return state.accountPublishingCompaniesEditingDialogDetails.shippingNumberRange },
    getAccountPublishingCompaniesDetailsCreditNumberRange(state)    { return state.accountPublishingCompaniesEditingDialogDetails.creditNumberRange },
    getAccountPublishingCompaniesDetailsStatementNumberRange(state){ return state.accountPublishingCompaniesEditingDialogDetails.statementNumberRange },
    getAccountPublishingCompaniesDetailsCwrSubmitterCode(state) { return state.accountPublishingCompaniesEditingDialogDetails.cwrSubmitterCode },
    getAccountPublishingCompaniesDetailsFooterMiddle(state)     { return state.accountPublishingCompaniesEditingDialogDetails.footerMiddle },

    // Column 4
    getAccountPublishingCompaniesDetailsInvoicesNextValue(state)    { return state.accountPublishingCompaniesEditingDialogDetails.invoicesNextValue },
    getAccountPublishingCompaniesDetailsShippingNextValue(state)    { return state.accountPublishingCompaniesEditingDialogDetails.shippingNextValue },
    getAccountPublishingCompaniesDetailsCreditNextValue(state)      { return state.accountPublishingCompaniesEditingDialogDetails.creditNextValue },
    getAccountPublishingCompaniesDetailsStatementNextValue(state)   { return state.accountPublishingCompaniesEditingDialogDetails.statementNextValue },
    getAccountPublishingCompaniesDetailsVatId(state)            { return state.accountPublishingCompaniesEditingDialogDetails.vatId },
    getAccountPublishingCompaniesDetailsFooterRight(state)      { return state.accountPublishingCompaniesEditingDialogDetails.footerRight },
    //#endregion
   
    //#region Label Companies PANEL (Getters)
    getAccountLabelCompanies(state)                             { return state.accountLabelCompanies },
    getSelectedAccountLabelCompanies(state)                     { return state.selectedAccountLabelCompanies },
    getAccountLabelCompaniesCompanyName(state)                  { return state.accountLabelCompanies.companyName },
    getShowAccountLabelCompaniesEditingDialog(state)            { return state.showAccountLabelCompaniesEditingDialog },
    //#endregion

    //#region Label Companies EDIT DIALOG (Getters)
    getDefaultAccountLabelCompanies(state)                      { return state.defaultAccountLabelCompanies },
    getAccountLabelCompaniesEditingDialogDetails(state)         { return state.accountLabelCompaniesEditingDialogDetails },
    getAddNewLabelCompany(state)                                { return state.addNewLabelCompany },

    // Column 1
    getAccountLabelCompaniesDetailsCompanyName(state)      { return state.accountLabelCompaniesEditingDialogDetails.companyName },
    getAccountLabelCompaniesDetailsAddressRow1(state)      { return state.accountLabelCompaniesEditingDialogDetails.addressRow1 },
    getAccountLabelCompaniesDetailsAddressRow2(state)      { return state.accountLabelCompaniesEditingDialogDetails.addressRow2 },
    getAccountLabelCompaniesDetailsZip(state)              { return state.accountLabelCompaniesEditingDialogDetails.zip },
    getAccountLabelCompaniesDetailsCity(state)             { return state.accountLabelCompaniesEditingDialogDetails.city },
    getAccountLabelCompaniesDetailsState(state)             { return state.accountLabelCompaniesEditingDialogDetails.state },
    getAccountLabelCompaniesDetailsCountry(state)          { return state.accountLabelCompaniesEditingDialogDetails.country },
    getAccountLabelCompaniesDetailsStatementText(state)    { return state.accountLabelCompaniesEditingDialogDetails.statementText },
 
    // Column 2
    getAccountLabelCompaniesDetailsSenderRow(state)        { return state.accountLabelCompaniesEditingDialogDetails.senderRow },
    getAccountLabelCompaniesDetailsGemaTasAudio(state)     { return state.accountLabelCompaniesEditingDialogDetails.gemaTasAudio },
    getAccountLabelCompaniesDetailsGemaTasVideo(state)     { return state.accountLabelCompaniesEditingDialogDetails.gemaTasVideo },
    getAccountLabelCompaniesDetailsGemaLicenseeNumber(state) { return state.accountLabelCompaniesEditingDialogDetails.gemaLicenseeNumber },
    getAccountLabelCompaniesDetailsFooterLeft(state)       { return state.accountLabelCompaniesEditingDialogDetails.footerLeft },

    // Column 3
    getAccountLabelCompaniesDetailsInvoicesNumberRange(state)   { return state.accountLabelCompaniesEditingDialogDetails.invoicesNumberRange },
    getAccountLabelCompaniesDetailsShippingNumberRange(state)   { return state.accountLabelCompaniesEditingDialogDetails.shippingNumberRange },
    getAccountLabelCompaniesDetailsCreditNumberRange(state)     { return state.accountLabelCompaniesEditingDialogDetails.creditNumberRange },
    getAccountLabelCompaniesDetailsStatementNumberRange(state)  { return state.accountLabelCompaniesEditingDialogDetails.statementNumberRange },
    getAccountLabelCompaniesDetailsFooterMiddle(state)          { return state.accountLabelCompaniesEditingDialogDetails.footerMiddle },

    // Column 4
    getAccountLabelCompaniesDetailsInvoicesNextValue(state)     { return state.accountLabelCompaniesEditingDialogDetails.invoicesNextValue },
    getAccountLabelCompaniesDetailsShippingNextValue(state)     { return state.accountLabelCompaniesEditingDialogDetails.shippingNextValue },
    getAccountLabelCompaniesDetailsCreditNextValue(state)       { return state.accountLabelCompaniesEditingDialogDetails.creditNextValue },
    getAccountLabelCompaniesDetailsStatementNextValue(state)    { return state.accountLabelCompaniesEditingDialogDetails.statementNextValue },
    getAccountLabelCompaniesDetailsFooterRight(state)           { return state.accountLabelCompaniesEditingDialogDetails.footerRight },
    getAccountLabelCompaniesDetailsVatId(state)                 { return state.accountLabelCompaniesEditingDialogDetails.vatId },
    getAccountLabelCompaniesDetailsCustomsId(state)             { return state.accountLabelCompaniesEditingDialogDetails.customsId },
    //#endregion

    //#region Invoices PANEL (Getters)
    getAccountInvoices(state)                           { return state.accountInvoices },
    //#endregion

    //#region Company Default Mappings DIALOG (Getters)
    getShowCompanyDefaultMappingsDialog(state)          { return state.showCompanyDefaultMappingsDialog },
    getCompanyDefaultMappingsContext(state)             { return state.companyDefaultMappingsContext },
    getCompanyDefaultMappings(state)                    { return state.companyDefaultMappings },
    getCompanyDefaultMappingsPools(state)               { return state.companyDefaultMappingsPools },
    //#endregion

    //ImageUploader
    getAccountPublishingCompaniesImageUrl(state)     { return state.selectedAccountPublishingCompanies.logo },
    getAccountLabelCompaniesImageUrl(state)          { return state.selectedAccountLabelCompanies.logo },
}

const mutations = { 
    setAccountUniqueId:                             (state, item)   => { state.accountUniqueId = item },
    setAccountDetails:                              (state, item)   => { state.accountDetails = item },
    setAccountDetailsCompanyName:                   (state, item)   => { state.accountDetails.companyName = item },
    setAccountDetailsAlvId:                         (state, item)   => { state.accountDetails.alvId = item },

    //#region MAIN FORM (Setters)
    // Column 1
    setAccountDetailsCompanyName:                   (state, item)   => { state.accountDetails.companyName = item },
    setAccountDetailsSalutation1:                   (state, item)   => { state.accountDetails.salutation1 = item },
    setAccountDetailsFirstname:                     (state, item)   => { state.accountDetails.firstname = item },
    setAccountDetailsLastname:                      (state, item)   => { state.accountDetails.lastname = item },
    setAccountDetailsAddressRow1:                   (state, item)   => { state.accountDetails.addressRow1 = item },
    setAccountDetailsAddressRow2:                   (state, item)   => { state.accountDetails.addressRow2 = item },
    setAccountDetailsZip:                           (state, item)   => { state.accountDetails.zip = item },
    setAccountDetailsCity:                          (state, item)   => { state.accountDetails.city = item },
    setAccountDetailsState:                         (state, item)   => { state.accountDetails.state = item },
    setAccountDetailsCountry:                       (state, item)   => { state.accountDetails.country = item },

    // Column 2
    setAccountDetailsCustomerNumber:                (state, item)   => { state.accountDetails.customerNumber = item },
    setAccountDetailsSalutation2:                   (state, item)   => { state.accountDetails.salutation2 = item },
    setAccountDetailsFormalSalutation:              (state, item)   => { state.accountDetails.formalSalutation = item },
    setAccountDetailsAccountHolder:                 (state, item)   => { state.accountDetails.accountHolder = item },
    setAccountDetailsIban:                          (state, item)   => { state.accountDetails.iban = item },
    setAccountDetailsBic:                           (state, item)   => { state.accountDetails.bic = item },
    setAccountDetailsEmail:                         (state, item)   => { state.accountDetails.email = item },
    setAccountDetailsCellphone:                     (state, item)   => { state.accountDetails.cellphone = item },
    setAccountDetailsPhone:                         (state, item)   => { state.accountDetails.phone = item },
    
    // Column 3
    setAccountDetailsAlvCode:                       (state, item)   => { state.accountDetails.alvCode = item },
    setAccountDetailsAlvPackage:                    (state, item)   => { state.accountDetails.alvPackage = item },
    setAccountDetailsVatPercent:                    (state, item)   => { state.accountDetails.vatPercent = item },
    setAccountDetailsSepaMandate:                   (state, item)   => { state.accountDetails.sepaMandate = item },
    setAccountDetailsSepaMandateDate:               (state, item)   => { state.accountDetails.sepaMandateDate = item },

    // Column 4
    setAccountDetailsLanguage:                      (state, item)   => { state.accountDetails.language = item },
    setAccountDetailsNumberFormat:                  (state, item)   => { state.accountDetails.numberFormat = item },
    setAccountDetailsCurrency:                      (state, item)   => { state.accountDetails.currency = item },
    setAccountDetailsHomeSocietyCode:               (state, item)   => { state.accountDetails.homeSocietyCode = item },
    setAccountDetailsStorageUsed:                   (state, item)   => { state.accountDetails.storageUsed = item },
    setAccountDetailsStorageAvailable:              (state, item)   => { state.accountDetails.storageAvailable = item },
    //#endregion

    //#region Publishing Companies PANEL (Setters)
    setAccountPublishingCompanies:                  (state, item)   => { state.accountPublishingCompanies = item },
    setSelectedAccountPublishingCompanies:          (state, item)   => { state.selectedAccountPublishingCompanies = item },
    setShowAccountPublishingCompaniesEditingDialog: (state, item)   => { state.showAccountPublishingCompaniesEditingDialog = item },
    setShowPayableConfirmationDialog:               (state, item)   => { state.showPayableConfirmationDialog = item },
    setPayableConfirmationDialogContext:            (state, context)   => { state.payableConfirmationDialogContext = context},
    //#endregion

    //#region Publishing Companies EDIT DIALOG (Setters)
    setDefaultAccountPublishingCompanies:               (state)         => { state.defaultAccountPublishingCompanies = DEFAULT_PUBLISHING_COMPANIES },
    setAccountPublishingCompaniesEditingDialogDetails:  (state, item)   => { state.accountPublishingCompaniesEditingDialogDetails = item },
    setAddNewPublishingCompany:                         (state, value)   => { state.addNewPublishingCompany = value },

    // Column 1
    setAccountPublishingCompaniesDetailsCompanyName:    (state, item)   => { state.accountPublishingCompaniesEditingDialogDetails.companyName = item },
    setAccountPublishingCompaniesDetailsCompanyAsAzEntry:(state, item)  => { state.accountPublishingCompaniesEditingDialogDetails.companyAsAzEntry = item },
    setAccountPublishingCompaniesDetailsCompanyAsAzList:(state, item)   => { state.accountPublishingCompaniesEditingDialogDetails.companyAsAzList = item },
    setAccountPublishingCompaniesDetailsAddressRow1:    (state, item)   => { state.accountPublishingCompaniesEditingDialogDetails.addressRow1 = item },
    setAccountPublishingCompaniesDetailsAddressRow2:    (state, item)   => { state.accountPublishingCompaniesEditingDialogDetails.addressRow2 = item },
    setAccountPublishingCompaniesDetailsZip:            (state, item)   => { state.accountPublishingCompaniesEditingDialogDetails.zip = item },
    setAccountPublishingCompaniesDetailsCity:           (state, item)   => { state.accountPublishingCompaniesEditingDialogDetails.city = item },
    setAccountPublishingCompaniesDetailsState:           (state, item)   => { state.accountPublishingCompaniesEditingDialogDetails.state = item },
    setAccountPublishingCompaniesDetailsCountry:        (state, item)   => { state.accountPublishingCompaniesEditingDialogDetails.country = item },
    setAccountPublishingCompaniesDetailsEmail:          (state, item)   => { state.accountPublishingCompaniesEditingDialogDetails.email = item },
    setAccountPublishingCompaniesDetailsStatementText:  (state, item)   => { state.accountPublishingCompaniesEditingDialogDetails.statementText = item },

    // Column 2
    setAccountPublishingCompaniesDetailsSenderRow:      (state, item)   => { state.accountPublishingCompaniesEditingDialogDetails.senderRow = item },
    setAccountPublishingCompaniesDetailsYkto:           (state, item)   => { state.accountPublishingCompaniesEditingDialogDetails.ykto = item },
    setAccountPublishingCompaniesDetailsHkto:           (state, item)   => { state.accountPublishingCompaniesEditingDialogDetails.hkto = item },
    setAccountPublishingCompaniesDetailsUkto:           (state, item)   => { state.accountPublishingCompaniesEditingDialogDetails.ukto = item },
    setAccountPublishingCompaniesDetailsIpi:            (state, item)   => { state.accountPublishingCompaniesEditingDialogDetails.ipi = item },
    setAccountPublishingCompaniesDetailsFooterLeft:     (state, item)   => { state.accountPublishingCompaniesEditingDialogDetails.footerLeft = item },

    // Column 3
    setAccountPublishingCompaniesDetailsInvoicesNumberRange:    (state, item)   => { state.accountPublishingCompaniesEditingDialogDetails.invoicesNumberRange = item },
    setAccountPublishingCompaniesDetailsShippingNumberRange:    (state, item)   => { state.accountPublishingCompaniesEditingDialogDetails.shippingNumberRange = item },
    setAccountPublishingCompaniesDetailsCreditNumberRange:      (state, item)   => { state.accountPublishingCompaniesEditingDialogDetails.creditNumberRange = item },
    setAccountPublishingCompaniesDetailsStatementNumberRange:   (state, item)   => { state.accountPublishingCompaniesEditingDialogDetails.statementNumberRange = item },
    setAccountPublishingCompaniesDetailsCwrSubmitterCode:       (state, item)   => { state.accountPublishingCompaniesEditingDialogDetails.cwrSubmitterCode = item },
    setAccountPublishingCompaniesDetailsFooterMiddle:           (state, item)   => { state.accountPublishingCompaniesEditingDialogDetails.footerMiddle = item },
 
    // Column 4
    setAccountPublishingCompaniesDetailsInvoicesNextValue:      (state, item)   => { state.accountPublishingCompaniesEditingDialogDetails.invoicesNextValue = item },
    setAccountPublishingCompaniesDetailsShippingNextValue:      (state, item)   => { state.accountPublishingCompaniesEditingDialogDetails.shippingNextValue = item },
    setAccountPublishingCompaniesDetailsCreditNextValue:        (state, item)   => { state.accountPublishingCompaniesEditingDialogDetails.creditNextValue = item },
    setAccountPublishingCompaniesDetailsStatementNextValue:     (state, item)   => { state.accountPublishingCompaniesEditingDialogDetails.statementNextValue = item },
    setAccountPublishingCompaniesDetailsVatId:                  (state, item)   => { state.accountPublishingCompaniesEditingDialogDetails.vatId = item },
    setAccountPublishingCompaniesDetailsFooterRight:            (state, item)   => { state.accountPublishingCompaniesEditingDialogDetails.footerRight = item },
    //#endregion

    //#region Label Companies PANEL (Setters)
    setAccountLabelCompanies:                       (state, item)   => { state.accountLabelCompanies = item = item },
    setSelectedAccountLabelCompanies:               (state, item)   => { state.selectedAccountLabelCompanies = item },
    setAccountLabelCompaniesCompanyName:            (state, item)   => { state.accountLabelCompanies.companyName = item },    
    setShowAccountLabelCompaniesEditingDialog:      (state, item)   => { state.showAccountLabelCompaniesEditingDialog = item },
    //#endregion

    //#region Label Companies EDIT DIALOG (Setters)
    setDefaultAccountLabelCompanies:                (state)         => { state.defaultAccountLabelCompanies = DEFAULT_LABEL_COMPANIES },
    setAccountLabelCompaniesEditingDialogDetails:   (state, item)   => { state.accountLabelCompaniesEditingDialogDetails = item },
    setAddNewLabelCompany:                          (state, value)   => { state.addNewLabelCompany = value },
    
    // Column 1
    setAccountLabelCompaniesDetailsCompanyName:    (state, item)   => { state.accountLabelCompaniesEditingDialogDetails.companyName = item },
    setAccountLabelCompaniesDetailsAddressRow1:    (state, item)   => { state.accountLabelCompaniesEditingDialogDetails.addressRow1 = item },
    setAccountLabelCompaniesDetailsAddressRow2:    (state, item)   => { state.accountLabelCompaniesEditingDialogDetails.addressRow2 = item },
    setAccountLabelCompaniesDetailsZip:            (state, item)   => { state.accountLabelCompaniesEditingDialogDetails.zip = item },
    setAccountLabelCompaniesDetailsCity:           (state, item)   => { state.accountLabelCompaniesEditingDialogDetails.city = item },
    setAccountLabelCompaniesDetailsState:           (state, item)   => { state.accountLabelCompaniesEditingDialogDetails.state = item },
    setAccountLabelCompaniesDetailsCountry:        (state, item)   => { state.accountLabelCompaniesEditingDialogDetails.country = item },
    setAccountLabelCompaniesDetailsStatementText:  (state, item)   => { state.accountLabelCompaniesEditingDialogDetails.statementText = item },

    // Column 2
    setAccountLabelCompaniesDetailsSenderRow:      (state, item)   => { state.accountLabelCompaniesEditingDialogDetails.senderRow = item },
    setAccountLabelCompaniesDetailsGemaTasAudio:   (state, item)   => { state.accountLabelCompaniesEditingDialogDetails.gemaTasAudio = item },
    setAccountLabelCompaniesDetailsGemaTasVideo:   (state, item)   => { state.accountLabelCompaniesEditingDialogDetails.gemaTasVideo = item },
    setAccountLabelCompaniesDetailsGemaLicenseeNumber: (state, item)   => { state.accountLabelCompaniesEditingDialogDetails.gemaLicenseeNumber = item },
    setAccountLabelCompaniesDetailsFooterLeft:     (state, item)   => { state.accountLabelCompaniesEditingDialogDetails.footerLeft = item },

    // Column 3
    setAccountLabelCompaniesDetailsInvoicesNumberRange:    (state, item)   => { state.accountLabelCompaniesEditingDialogDetails.invoicesNumberRange = item },
    setAccountLabelCompaniesDetailsShippingNumberRange:    (state, item)   => { state.accountLabelCompaniesEditingDialogDetails.shippingNumberRange = item },
    setAccountLabelCompaniesDetailsCreditNumberRange:      (state, item)   => { state.accountLabelCompaniesEditingDialogDetails.creditNumberRange = item },
    setAccountLabelCompaniesDetailsStatementNumberRange:  (state, item)   => { state.accountLabelCompaniesEditingDialogDetails.statementNumberRange = item },
    setAccountLabelCompaniesDetailsFooterMiddle:           (state, item)   => { state.accountLabelCompaniesEditingDialogDetails.footerMiddle = item },
 
    // Column 4
    setAccountLabelCompaniesDetailsInvoicesNextValue:      (state, item)   => { state.accountLabelCompaniesEditingDialogDetails.invoicesNextValue = item },
    setAccountLabelCompaniesDetailsShippingNextValue:      (state, item)   => { state.accountLabelCompaniesEditingDialogDetails.shippingNextValue = item },
    setAccountLabelCompaniesDetailsCreditNextValue:        (state, item)   => { state.accountLabelCompaniesEditingDialogDetails.creditNextValue = item },
    setAccountLabelCompaniesDetailsStatementNextValue:     (state, item)   => { state.accountLabelCompaniesEditingDialogDetails.statementNextValue = item },
    setAccountLabelCompaniesDetailsFooterRight:            (state, item)   => { state.accountLabelCompaniesEditingDialogDetails.footerRight = item },
    setAccountLabelCompaniesDetailsVatId:                  (state, item)   => { state.accountLabelCompaniesEditingDialogDetails.vatId = item },
    setAccountLabelCompaniesDetailsCustomsId:              (state, item)   => { state.accountLabelCompaniesEditingDialogDetails.customsId = item },
    //#endregion

    //#region Invoices PANEL (Setters)
    setAccountInvoices:                                     (state, item)   => { state.accountInvoices = item },
    //#endregion

    //#region Company Default Mappings DIALOG (Setters)
    setShowCompanyDefaultMappingsDialog:                    (state, item)   => { state.showCompanyDefaultMappingsDialog = item },
    setCompanyDefaultMappingsContext:                       (state, item)   => { state.companyDefaultMappingsContext = item || { companyId: null, companyType: null, companyName: null } },
    setCompanyDefaultMappings:                              (state, item)   => { state.companyDefaultMappings = typeof item === 'object' && item !== null ? Object.values(item) : [] },
    // pool cache helpers: write/clear via Vue.set so reactivity works on a plain object map
    setCompanyDefaultMappingsPoolEntry:                     (state, { key, value }) => { Vue.set(state.companyDefaultMappingsPools, key, value) },
    clearCompanyDefaultMappingsPools:                       (state)         => { state.companyDefaultMappingsPools = {} },
    //#endregion

    //ImageUploader
    setAccountPublishingCompaniesImageUrl:                  (state, item)   => { state.selectedAccountPublishingCompanies.logo = item },
    setAccountLabelCompaniesImageUrl:                       (state, item)   => { state.selectedAccountLabelCompanies.logo = item },
}

const actions = {

    async fetchAccountDetails({ commit, getters, dispatch }) {
        let values
        let userCustomerNumber = getters.getUserCustomerNumber
        let response = await api.getDetailsById(DIR, userCustomerNumber) 
        if(response === undefined || response.length === 0 || response.error === 1) {
            values = {}
        } else {
            values = response
        }

        let userDataLocalStorage = JSON.parse(localStorage.getItem('userData'))
        let oldHomeSocietyCodeInLocalStorage = userDataLocalStorage.user.homeSocietyCode
        let newSelectedHomeSocietyCode = response.homeSocietyCode.toString()

        commit('setAccountDetails', values)
        dispatch('fetchAccountPublishingCompanies')
        dispatch('fetchAccountLabelCompanies')
        if(oldHomeSocietyCodeInLocalStorage !== newSelectedHomeSocietyCode){
            dispatch('changeHomeSocietyInLocalStorage')
        }
        commit('setIsLoading', false)
    },

    // FETCH PUBLISHING COMPANIES
    async fetchAccountPublishingCompanies({ commit, getters }){
        let publishingCompaniesFromDB = await getters.getAccountDetails.publishingCompanies
        if (publishingCompaniesFromDB === undefined) {
            return null
        }
        else {
            commit('setIsLoading', false)
            if(publishingCompaniesFromDB.length === 0) commit('setNoDataText', 'lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')
            commit('setAccountPublishingCompanies', publishingCompaniesFromDB)
        }
    },

    // FETCH LABEL COMPANIES
    async fetchAccountLabelCompanies({ commit, getters }){
        let labelCompaniesFromDB = await getters.getAccountDetails.labelCompanies
        if (labelCompaniesFromDB === undefined) {
            return null
        }
        else {
            commit('setIsLoading', false)
            if(labelCompaniesFromDB.length === 0) commit('setNoDataText', 'lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')
            commit('setAccountLabelCompanies', labelCompaniesFromDB)
        }
    },

    // FETCH AZ ENTRIES on creation of PUBLISHING COMPANY dialog
    async fetchAzEntries({ commit, getters }) {
        let azEntries = await getters.getAccountPublishingCompaniesDetailsCompanyAsAzList
        commit('setcompanyAsAzList', azEntries)
    },

    // FETCH INVOICES on creation of Panel
    async fetchAccountInvoices({ commit, getters }) {
        let customerNumber = await getters.getUserCustomerNumber
        let response = await api.getAccountInvoices(customerNumber)
        commit('setAccountInvoices', response.list)
    },

    // SAVE
    async saveAccountForm({ commit, getters, dispatch }) {
        let alvId = getters.getAccountDetailsAlvId
        
        // getters.getAccountDetails.publishingCompanies.companyAsAzEntry = getters.getAccountPublishingCompaniesDetailsCompanyAsAzEntry

        let metaData = getters.getAccountDetails

        let response = await api.saveEditForm(DIR, alvId, metaData)  // isNew als Parameter entfernt

        if(response === undefined || response.length === 0 || response.error === 1) { 
            response = []
            commit('setSnackbar', { 
                type: 'error', 
                msg: i18n.t('lang_GENERIC_SNACKBAR_SAVING_NOT_SUCCESSFUL'),
            })
        } else {
            commit('setSnackbar', { 
                type: 'success', 
                msg: i18n.t('lang_GENERIC_SNACKBAR_SAVING_SUCCESSFUL')
            })
            // refresh data
            await dispatch('fetchAccountDetails',  response.response.alvId)
            
            //Updating local storage after adding a new publishing company 
            let publishingCompanies = getters.getAccountPublishingCompanies
            let newPublishingCompanies  = []

            publishingCompanies.forEach(element => {
                newPublishingCompanies.push(
                    {
                        "alvId" : element.alvId,
                        "cwr":element.cwrSubmitterCode,
                        "id":element.id,
                        "name": element.companyName
                    }
                )
            });
            //Updating local storage after adding a new label
            let labelCompanies = getters.getAccountLabelCompanies
            let newLabelCompanies  = []

            labelCompanies.forEach(element => {
                newLabelCompanies.push(
                    {
                        "alvId" : element.alvId,
                        "labels":[],
                        "id":element.id,
                        "name": element.companyName
                    }
                )
            }); 
            let localStorageData = JSON.parse(localStorage.getItem("userData"))
            localStorageData.publishingCompanies=newPublishingCompanies
            localStorageData.labelCompanies=newLabelCompanies
            localStorageData.user.companyCountry = getters.getAccountDetailsCountry
           localStorage.setItem("userData",JSON.stringify(localStorageData))

            commit('setPublishingCompanies')
            commit('setLabelCompanies')
        }
    },
    changeHomeSocietyInLocalStorage({getters}){
        let userDataLocalStorage = JSON.parse(localStorage.getItem('userData'))
        let oldHomeSocietyCodeInLocalStorage = userDataLocalStorage.user.homeSocietyCode
        let newSelectedHomeSocietyCode = getters.getAccountDetailsHomeSocietyCode

        if(oldHomeSocietyCodeInLocalStorage !== newSelectedHomeSocietyCode){
            let societiesArray = userDataLocalStorage.societies
            let newSelectedHomeSocietyName = societiesArray.find(society => {
                return society.value === newSelectedHomeSocietyCode
            }).text

            userDataLocalStorage.user.homeSocietyCode = newSelectedHomeSocietyCode.toString()
            userDataLocalStorage.user.homeSociety = newSelectedHomeSocietyName

            localStorage.setItem('userData', JSON.stringify(userDataLocalStorage))
        }
    },

    // Open dialog for a specific (publishing|label) company. Resets prior state
    // and fetches the current `defaults` list from BE.
    async openCompanyDefaultMappingsDialog({ commit, dispatch }, { companyId, companyType, companyName }) {
        commit('setCompanyDefaultMappingsContext', { companyId, companyType, companyName })
        commit('setCompanyDefaultMappings', [])
        commit('clearCompanyDefaultMappingsPools')
        commit('setShowCompanyDefaultMappingsDialog', true)
        await dispatch('fetchCompanyDefaultMappings')
    },

    // GET /account/settings/company-default-mappings
    async fetchCompanyDefaultMappings({ commit, getters }) {
        let ctx = getters.getCompanyDefaultMappingsContext
        if (!ctx || !ctx.companyId || !ctx.companyType) return

        let response = await api.getCompanyDefaultMappings(ctx.companyId, ctx.companyType)
        if (response && response.defaults) {
            commit('setCompanyDefaultMappings', response.defaults)
        } else {
            commit('setCompanyDefaultMappings', [])
            if (response && response.error === 1) {
                commit('setSnackbar', {
                    type: 'error',
                    msg: i18n.t('lang_GENERIC_MESSAGE_ERROR_UNKNOWN'),
                })
            }
        }
    },

    // Fetch and cache the candidate mappings pool for a whole domain
    // ('publishing_statements' or 'label_statements'). One request covers every
    // statement_type + language combination in that domain; cells filter from
    // this list client-side. In-flight requests are deduplicated via a Promise.
    async fetchCompanyDefaultMappingsDomainPool({ commit, getters }, { domain }) {
        if (!domain) return { all: [] }
        let key = domain
        let pools = getters.getCompanyDefaultMappingsPools
        if (pools[key]) {
            // Cache hit: either resolved data, in-flight promise, or prior error.
            if (pools[key].promise) return pools[key].promise
            if (!pools[key].error) return pools[key]
        }

        let promise = (async () => {
            // The publishing pool spans two mapping domains: the five classic
            // statement sheets and the payee sheet, which has its own
            // placeholder set. BE accepts a comma-separated domain list.
            let filterObj = {
                'direction': 'export',
                'domain':    domain === 'publishing_statements'
                    ? ['publishing_statements', 'publishing_payee_statement']
                    : domain,
                'maintainer': 'self+alv'
            }
            let paramsString = this._vm.$alvPlugin.createApiUrlFilterString(filterObj)
            let response = await api.getListData('account/mappings', '1', paramsString)

            if (!response || response.error === 1 || !Array.isArray(response.list)) {
                let errValue = { all: [], loading: false, error: true }
                commit('setCompanyDefaultMappingsPoolEntry', { key, value: errValue })
                return errValue
            }

            // Normalize once at fetch time so cells just filter + group.
            let all = response.list.map(m => ({
                value:         m.id,                                // PUT contract uses numeric mappingId
                alvId:         m.alvId,
                text:          m.abbreviation || m.title || m.alvId,
                title:         m.title,
                language:      m.language,
                shareable:     m.shareable,
                domain:        m.domain,                            // cells filter their slot's domain out of the pool
                statementType: m.statementType || m.statement_type, // be tolerant of either casing
                maintainer:     m.maintainer,
            }))

            let value = { all, loading: false }
            commit('setCompanyDefaultMappingsPoolEntry', { key, value })
            return value
        })()

        // Park the in-flight promise so concurrent cell mounts (5x publishing) share it.
        commit('setCompanyDefaultMappingsPoolEntry', { key, value: { all: [], loading: true, promise } })
        return promise
    },

    // PUT a single (mappingType, language, mappingId) upsert. mappingId === null deletes.
    // Replaces local defaults from the response (BE is the truth).
    async saveCompanyDefaultMapping({ commit, getters }, { mappingType, language, mappingId }) {
        let ctx = getters.getCompanyDefaultMappingsContext
        if (!ctx || !ctx.companyId || !ctx.companyType) return { ok: false }

        let payload = {
            companyId:   ctx.companyId,
            companyType: ctx.companyType,
            items: [{ mappingType, language, mappingId: mappingId == null ? null : mappingId }],
        }

        let response = await api.putCompanyDefaultMappings(payload)
        if (response && response.defaults) {
            commit('setCompanyDefaultMappings', response.defaults)
            return { ok: true }
        }
        commit('setSnackbar', {
            type: 'error',
            msg: i18n.t('lang_GENERIC_SNACKBAR_SAVING_NOT_SUCCESSFUL'),
        })
        return { ok: false }
    },
}

export default {
    state, getters, actions, mutations
}